import React, { useEffect, useMemo } from "react";
import {
  Tab,
  Tabs,
  Card,
  ButtonGroup,
  ToggleButton,
  Table,
  Tooltip,
} from "react-bootstrap";
import AnalyticsChart from "@/components/Home/AnalyticsChart";
import MediaList from "../MediaList/MediaList";
import { QuickActions } from "@/components/Home/QuickActions";
import { SocialDistribution } from "@/components/Home/SocialDistribution";
import "../../../scss/dashboard.scss";
import { connect, ConnectedProps } from "react-redux";
import { RootState } from "../../../store";
import { useState } from "react";
import CreateOptions from "@/components/Create/CreateOptions";
import { Modal, Button, Skeleton } from "../../../components";
import { MediaDetailConfig } from "@/utils/utilFunctions/Mapper/MediaDetailConfig";
import MediaDetails from "../MediaDetail/MediaDetails";
import MediaPublish from "../MediaPublish/MediaPublish";
import { useScrollToTop } from "../../../hooks/useScrollToTop";
import {
  emptyAssetDetails,
  emptyMediaDetails,
} from "@/store/media/media.slice";
import { getHomeList, getMedia } from "@/store/media/media.action";
import { callRegisterMediaOnCms } from "@/store/upload/upload.action";
import {
  setUploadedSuccessfully,
  updateFileProgress,
  setUploadingError,
  decreaseCounter,
} from "@/store/upload/upload.slice";
import {
  setSoftRefresh,
  showUploadProgress,
  updateModal,
} from "@/store/app/app.slice";
import {
  getUpdateMedia,
  updateIsFilter,
  updateIsClearFilter,
  updateActiveCustomUploadTab,
} from "@/store/media/media.action";
import { setisFilter, setMediaList } from "@/store/media/media.slice";
import { updateMediaStatus } from "@/store/media/media.slice";
import { createOptionsFileEvent } from "@/utils/createOptionsUtils";
import DiscoverCard from "@/components/Discover/DiscoverCard";
import {
  getConsistentColor,
  SOCIAL_DOMAIN_MAPPER,
  THUMBNAIL_SIZE_SUFFIX,
  applyThumbnailSize,
} from "../../../utils";
import { ChevronRight } from "lucide-react";
import PageHeader from "@/components/Layout/PageHeader";
import FailureThumb from "../../../assets/icons/svg/failure.svg";
import { YOUTUBE_URL } from "../../../utils";
import { NoContentFound } from "@/utils/ui/NoContent";

const CHART_SCRIPT_URL = "https://www.gstatic.com/charts/loader.js";

interface HourlyDistributionItem {
  doc_count: number;
  hour: number;
  key: number;
  time_label: string;
}

const Home = ({
  emptyAssetDetails,
  emptyMediaDetails,
  getMedia,
  bunchProductList,
  callRegisterMediaOnCms,
  updateFileProgress,
  showUploadProgress,
  setUploadedSuccessfully,
  setSoftRefresh,
  decreaseCounter,
  updateIsFilter,
  updateIsClearFilter,
  updateActiveCustomUploadTab,
  activeCustomUploadTab,

  updateModal,
  mediaList,
  getHomeList,
  homeList,
  currentMediaDetailsForEdit,
}: Props) => {
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [showWizardModal, setShowWizardModal] = useState<boolean>(false);
  const [publishModal, setPublishModal] = useState<boolean>(false);
  const [activeContentType, setActiveContentType] = useState<
    "youtube" | "onplatform"
  >("onplatform");
  const [activeLatestContent, setActiveLatestContent] = useState<
    "schedule" | "draft"
  >("schedule");
  const [modalContent, setModalContent] = useState<React.ReactNode>(null);
  const [modalTitle, setModalTitle] = useState<string>("CREATE");
  const [modalFooter, setModalFooter] = useState<React.ReactNode>(null);
  const [premiereNextDisabled, setPremiereNextDisabled] = useState(true);
  const [premiereRpcPending, setPremiereRpcPending] = useState(false);
  const [premiereData, setPremiereData] = useState<any>(null);
  const [initialOption, setInitialOption] = useState<string | undefined>(
    undefined
  );
  const [currentAssetType, setCurrentAssetType] = useState<string>("video");
  const { navigateWithScroll } = useScrollToTop();
  const youtubeVideos = homeList?.youtube_data_agg?.videos || [];
  const onPlatformVideos = homeList?.video_analytics_agg?.videos || [];
  const currentVideos =
    activeContentType === "youtube" ? youtubeVideos : onPlatformVideos;

  const latestContentVideos =
    activeLatestContent === "schedule"
      ? homeList?.social_embeds_agg?.recent_videos || []
      : homeList?.social_embeds_agg?.socialembeds?.queued_videos || [];
  const mediaDetailConfig = MediaDetailConfig(
    (currentMediaDetailsForEdit?.asset_type || "video") as any
  );
  const [showIframe, setShowIframe] = useState(false);

  const navigateToMediaPage = () => {
    const assetType =
      currentMediaDetailsForEdit?.asset_type || currentAssetType;
    if (assetType) {
      navigateWithScroll(`/media/${assetType}`);
    }
  };

  const handleCreateClose = () => {
    setShowCreateModal(false);
  };

  const handleWizardClose = () => {
    updateIsClearFilter && updateIsClearFilter(true);
    updateIsFilter && updateIsFilter(false);
    navigateToMediaPage();
    emptyMediaDetails();
    emptyAssetDetails();
    setPremiereData(null);
    setShowWizardModal(false);
  };

  const updateModalContent = (
    content: React.ReactNode,
    title: string,
    footer?: React.ReactNode
  ) => {
    setModalContent(content);
    setModalTitle(title);
    setModalFooter(footer);
  };

  const openUpload = () => {
    setShowWizardModal(true);
    setShowCreateModal(false);
  };

  const handleNextClick = () => {
    const event = new CustomEvent("nextClick");
    window.dispatchEvent(event);
  };

  const handleBackClickEvent = () => {
    const event = new CustomEvent("backClick", {
      detail: { id: currentMediaDetailsForEdit?.id },
    });
    window.dispatchEvent(event);
  };

  const handleDone = () => {
    const event = new CustomEvent("doneClick");
    window.dispatchEvent(event);
    navigateToMediaPage();
  };

  useEffect(() => {
    const handleBackClick = (event: CustomEvent) => {
      const id = event.detail?.id;
      getMedia({ id: id || currentMediaDetailsForEdit?.id });
      setPublishModal(false);
      setShowWizardModal(true);
    };
    window.addEventListener("backClick", handleBackClick as EventListener);
    return () => {
      window.removeEventListener("backClick", handleBackClick as EventListener);
    };
  }, [getMedia, currentMediaDetailsForEdit]);

  useEffect(() => {
    emptyAssetDetails();
    emptyMediaDetails();
  }, [emptyAssetDetails, emptyMediaDetails]);

  // Listen for asset type changes from forms
  useEffect(() => {
    const handleAssetTypeChange = (event: CustomEvent) => {
      const assetType = event.detail?.assetType;
      if (assetType) {
        setCurrentAssetType(assetType);
      }
    };

    window.addEventListener(
      "assetTypeChange",
      handleAssetTypeChange as EventListener
    );
    return () => {
      window.removeEventListener(
        "assetTypeChange",
        handleAssetTypeChange as EventListener
      );
    };
  }, []);

  useEffect(() => {
    getHomeList({});
  }, [getHomeList]);

  const calculatePercentageChange = (
    todayData: HourlyDistributionItem[] | undefined,
    yesterdayData: HourlyDistributionItem[] | undefined
  ): string => {
    const currentHour = new Date().getHours();
    const todayFiltered =
      todayData?.filter((item) => item.hour <= currentHour) || [];
    const yesterdayFiltered =
      yesterdayData?.filter((item) => item.hour <= currentHour) || [];
    const todayCountUpToCurrentHour = todayFiltered.reduce((total, item) => {
      return total + (item.doc_count || 0);
    }, 0);

    const yesterdayCountUpToCurrentHour = yesterdayFiltered.reduce(
      (total, item) => {
        return total + (item.doc_count || 0);
      },
      0
    );

    if (!yesterdayCountUpToCurrentHour || yesterdayCountUpToCurrentHour === 0) {
      const result = todayCountUpToCurrentHour > 0 ? "+100%" : "0%";
      return result;
    }

    const change =
      ((todayCountUpToCurrentHour - yesterdayCountUpToCurrentHour) /
        yesterdayCountUpToCurrentHour) *
      100;
    const sign = change >= 0 ? "+" : "";
    const result = `${sign}${change.toFixed(1)}%`;
    return result;
  };

  const getTotalCount = (
    data: HourlyDistributionItem[] | undefined
  ): number => {
    if (!data || !Array.isArray(data)) {
      return 0;
    }
    const total = data.reduce((total: number, item: HourlyDistributionItem) => {
      const count = item.doc_count || 0;
      return total + count;
    }, 0);
    return total;
  };

  const contentUploadedToday = getTotalCount(
    homeList?.media_today_agg?.hourly_distribution_content_uploaded
  );

  const contentUploadedChange = useMemo(() => {
    if (
      homeList?.isLoading ||
      !homeList?.media_today_agg?.hourly_distribution_content_uploaded ||
      !homeList?.media_yesterday_agg?.hourly_distribution_content_uploaded
    ) {
      return "0%";
    }
    const todayHasData =
      homeList.media_today_agg.hourly_distribution_content_uploaded.some(
        (item: HourlyDistributionItem) => item.doc_count > 0
      );
    const yesterdayHasData =
      homeList.media_yesterday_agg.hourly_distribution_content_uploaded.some(
        (item: HourlyDistributionItem) => item.doc_count > 0
      );

    if (!todayHasData && !yesterdayHasData) {
      return "0%";
    }
    const result = calculatePercentageChange(
      homeList.media_today_agg.hourly_distribution_content_uploaded,
      homeList.media_yesterday_agg.hourly_distribution_content_uploaded
    );
    return result;
  }, [
    homeList?.isLoading,
    homeList?.media_today_agg?.hourly_distribution_content_uploaded,
    homeList?.media_yesterday_agg?.hourly_distribution_content_uploaded,
  ]);

  const platformPublishedToday = getTotalCount(
    homeList?.media_today_agg?.hourly_distribution_by_denmark
  );

  const platformPublishedChange = useMemo(() => {
    if (
      homeList?.isLoading ||
      !homeList?.media_today_agg?.hourly_distribution_by_denmark ||
      !homeList?.media_yesterday_agg?.hourly_distribution_by_denmark
    ) {
      return "0%";
    }
    return calculatePercentageChange(
      homeList.media_today_agg.hourly_distribution_by_denmark,
      homeList.media_yesterday_agg.hourly_distribution_by_denmark
    );
  }, [
    homeList?.isLoading,
    homeList?.media_today_agg?.hourly_distribution_by_denmark,
    homeList?.media_yesterday_agg?.hourly_distribution_by_denmark,
  ]);

  const socialPublishedToday = getTotalCount(
    homeList?.media_today_agg?.hourly_distribution_by_social
  );

  const socialPublishedChange = useMemo(() => {
    if (
      homeList?.isLoading ||
      !homeList?.media_today_agg?.hourly_distribution_by_social ||
      !homeList?.media_yesterday_agg?.hourly_distribution_by_social
    ) {
      return "0%";
    }
    return calculatePercentageChange(
      homeList.media_today_agg.hourly_distribution_by_social,
      homeList.media_yesterday_agg.hourly_distribution_by_social
    );
  }, [
    homeList?.isLoading,
    homeList?.media_today_agg?.hourly_distribution_by_social,
    homeList?.media_yesterday_agg?.hourly_distribution_by_social,
  ]);

  const onFileEvent = (
    event: string,
    data: {
      file: {
        fileName: string;
        uniqueIdentifier: string;
        size: number;
        progress: (arg: boolean) => number;
      };
      isShorts?: boolean;
      asset_type?: string;
      id?: string;
      type?: string;
    },
    cb?: (
      query: { entity: string; key: string; method: string },
      token: string
    ) => void
  ): void => {
    return createOptionsFileEvent(event, data, cb, {
      showUploadProgress,
      callRegisterMediaOnCms,
      updateIsFilter,
      updateIsClearFilter,
      emptyMediaDetails,
      emptyAssetDetails,
      getMedia,
      updateFileProgress,
      decreaseCounter,
      setUploadedSuccessfully,
      setSoftRefresh,
      setUploadingError,
      openUpload,
    });
  };

  const handleClick = (option: string) => {
    if (option === "CUSTOM_UPLOADS" || option === "EMBED") {
      setInitialOption(option);
      setModalContent(null);
      setModalTitle("CREATE");
      setModalFooter(null);
      setShowCreateModal(true);
    }
  };

  const handleVideoClick = (video: any) => {
    if (activeContentType === "youtube" && video.video_id) {
      const youtubeVideoUrl = `${YOUTUBE_URL}${video.video_id}`;
      window.open(youtubeVideoUrl, "_blank");
    } else {
      const data = video?.social_embeds[0];
      const getBaseUrl = SOCIAL_DOMAIN_MAPPER[data?.host];
      const denmarkUrl = data?.url;
      if (getBaseUrl) {
        window.open(`${getBaseUrl}/${denmarkUrl}`, "_blank");
      }
    }
  };

  return (
    <div className="dashboard_container">
      <PageHeader title="DASHBOARD" />
      <div className="row align-items-start m-0 mt-3">
        <div className="col-lg-9 col-md-8 col-sm-12">
          <div className="">
            <Card className="mb-3">
              <Card.Body className="p-0">
                <Tabs defaultActiveKey="overview" className="mb-0" fill>
                  <Tab
                    eventKey="overview"
                    title={
                      <div className="py-2 h-100">
                        <div className="fw-medium text-muted">
                          Content Uploaded
                        </div>
                        <div className="f-30 fw-bold mt-3 mb-2">
                          {contentUploadedToday.toString()}
                        </div>
                        <div className="fst-italic f-10 fw-normal">
                          <span
                            className={
                              contentUploadedChange.startsWith("+")
                                ? "text-success"
                                : "text-danger"
                            }
                          >
                            {contentUploadedChange}
                          </span>{" "}
                          {contentUploadedChange.startsWith("+")
                            ? "more"
                            : "less"}{" "}
                          than yesterday
                        </div>
                      </div>
                    }
                  >
                    <div className="p-3" style={{ width: "100%" }}>
                      <AnalyticsChart
                        key={`content-uploaded-${JSON.stringify(
                          homeList?.media_today_agg
                        )}`}
                        updateModal={updateModal}
                        CHART_SCRIPT_URL={CHART_SCRIPT_URL}
                        homeList={{
                          media_today_agg: homeList?.media_today_agg,
                          media_yesterday_agg: homeList?.media_yesterday_agg,
                        }}
                        dataField="hourly_distribution_content_uploaded"
                      />
                    </div>
                  </Tab>
                  <Tab
                    eventKey="analytics"
                    title={
                      <div className="py-2 h-100">
                        <div className="fw-medium text-muted">
                          Platform Published
                        </div>
                        <div className="f-30 fw-bold mt-3 mb-2">
                          {platformPublishedToday.toString()}
                        </div>
                        <div className="fst-italic f-10 fw-normal">
                          <span
                            className={
                              platformPublishedChange.startsWith("+")
                                ? "text-success"
                                : "text-danger"
                            }
                          >
                            {platformPublishedChange}
                          </span>{" "}
                          {platformPublishedChange.startsWith("+")
                            ? "more"
                            : "less"}{" "}
                          than yesterday
                        </div>
                      </div>
                    }
                  >
                    <div className="p-3" style={{ width: "100%" }}>
                      <AnalyticsChart
                        key={`platform-published-${JSON.stringify(
                          homeList?.media_today_agg
                        )}`}
                        updateModal={updateModal}
                        CHART_SCRIPT_URL={CHART_SCRIPT_URL}
                        homeList={{
                          media_today_agg: homeList?.media_today_agg,
                          media_yesterday_agg: homeList?.media_yesterday_agg,
                        }}
                        dataField="hourly_distribution_by_denmark"
                      />
                    </div>
                  </Tab>
                  <Tab
                    eventKey="performance"
                    title={
                      <div className="py-2 h-100">
                        <div className="fw-medium text-muted">
                          Social Published
                        </div>
                        <div className="f-30 fw-bold mt-3 mb-2">
                          {socialPublishedToday.toString()}
                        </div>
                        <div className="fst-italic f-10 fw-normal">
                          <span
                            className={
                              socialPublishedChange.startsWith("+")
                                ? "text-success"
                                : "text-danger"
                            }
                          >
                            {socialPublishedChange}
                          </span>{" "}
                          {socialPublishedChange.startsWith("+")
                            ? "more"
                            : "less"}{" "}
                          than yesterday
                        </div>
                      </div>
                    }
                  >
                    <div className="p-3" style={{ width: "100%" }}>
                      <AnalyticsChart
                        key={`social-published-${JSON.stringify(
                          homeList?.media_today_agg
                        )}`}
                        updateModal={updateModal}
                        CHART_SCRIPT_URL={CHART_SCRIPT_URL}
                        homeList={{
                          media_today_agg: homeList?.media_today_agg,
                          media_yesterday_agg: homeList?.media_yesterday_agg,
                        }}
                        dataField="hourly_distribution_by_social"
                      />
                    </div>
                  </Tab>
                </Tabs>
              </Card.Body>
            </Card>
          </div>
          <div className="row m-0">
            <div className="col-sm-12 col-md-7 ps-0">
              <div className="card mb-3">
                <div className="d-flex justify-content-between align-items-center p-3">
                  <div className="">
                    <div className="f-14 fw-medium text-uppercase">
                      Top Performing Contents
                    </div>
                    <div className="f-10 text-muted fw-italic ">
                      Since Midnight
                    </div>
                  </div>
                  <ButtonGroup className="switch-buttons gap-2">
                    <ToggleButton
                      id="onplatform-toggle"
                      type="radio"
                      variant={
                        activeContentType === "onplatform"
                          ? "primary"
                          : "outline-primary"
                      }
                      name="content-type"
                      value="onplatform"
                      checked={activeContentType === "onplatform"}
                      onChange={(e) => setActiveContentType("onplatform")}
                      size="sm"
                    >
                      On Platform
                    </ToggleButton>
                    <ToggleButton
                      id="youtube-toggle"
                      type="radio"
                      variant={
                        activeContentType === "youtube"
                          ? "primary"
                          : "outline-primary"
                      }
                      name="content-type"
                      value="youtube"
                      checked={activeContentType === "youtube"}
                      onChange={(e) => setActiveContentType("youtube")}
                      size="sm"
                    >
                      YouTube
                    </ToggleButton>
                  </ButtonGroup>
                </div>
                <div
                  className="w-100 overflow-hidden"
                  style={{ minHeight: "498px" }}
                >
                  {/* Header Row */}
                  <div className="row g-0 bg-light px-3 py-2 fw-bold text-muted small">
                    <div className="col-9">Content</div>
                    <div className="col-3 text-center">Views</div>
                    {/* <div className="col-2 text-center">Avg. View %</div> */}
                  </div>

                  {/* Content Rows */}
                  <div className="w-100">
                    {homeList?.isLoading ? (
                      [...Array(5)].map((_, index) => (
                        <div
                          key={index}
                          className="row g-0 border-bottom p-3 align-items-center"
                        >
                          <div className="col-9">
                            <div className="d-flex flex-wrap align-items-center gap-2">
                              <Skeleton
                                type="thumbnail"
                                width={50}
                                height={28}
                              />
                              <Skeleton type="text" width="60%" />
                            </div>
                          </div>
                          <div className="col-2 text-center">
                            <Skeleton type="text" width={60} />
                          </div>
                          <div className="col-2 text-center">
                            <Skeleton type="text" width={40} />
                          </div>
                        </div>
                      ))
                    ) : currentVideos.length > 0 ? (
                      currentVideos.map((video: any) => (
                        <div
                          key={video.video_id}
                          onClick={() => handleVideoClick(video)}
                          className={`row g-0 border-bottom px-3 py-2 align-items-center ${
                            activeContentType === "youtube"
                              ? "table-row-hover"
                              : ""
                          }`}
                          style={{
                            cursor:
                              activeContentType === "youtube"
                                ? "pointer"
                                : "default",
                          }}
                        >
                          <div className="col-9">
                            <div className="d-flex flex-wrap align-items-center gap-2">
                              <div
                                className="ratio ratio-16x9 me-2"
                                style={{ flex: "0 0 50px" }}
                              >
                                <img
                                  src={
                                    activeContentType === "onplatform"
                                      ? applyThumbnailSize(video.thumbnail_url)
                                      : video.thumbnail_url
                                  }
                                  alt="Video thumbnail"
                                  onError={(e) => {
                                    e.currentTarget.src = FailureThumb;
                                  }}
                                />
                              </div>
                              <div
                                className="col-9 anchor text-truncate flex-grow-1"
                                style={{ maxWidth: "calc(100% - 70px)" }}
                              >
                                {video.title}
                              </div>
                            </div>
                          </div>
                          <div className="col-3 text-center text-secondary">
                            {new Intl.NumberFormat("en-IN").format(
                              video.view_count
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <NoContentFound
                        message="No videos available"
                        height="200px"
                      />
                    )}
                  </div>
                  {showIframe && (
                    <Modal
                      isOpen={showIframe}
                      onClose={() => setShowIframe(false)}
                      title="Analytics Dashboard"
                      width="90%"
                      height="90%"
                      updateModal={updateModal}
                    >
                      <iframe
                        src={process.env.NX_APP_ANALYTICS ?? ""}
                        className="w-100 h-100 border-0"
                        title="Analytics Dashboard"
                      />
                    </Modal>
                  )}
                </div>
              </div>
            </div>
            <div className="col-sm-12 col-md-5 pe-0">
              <div className="card mb-3">
                <div className="px-3 pt-3">
                  <div className="f-14 fw-medium text-uppercase">
                    Latest Content
                  </div>
                  <div className="f-10 text-muted fw-italic ">
                    Past 48 hours
                  </div>
                  <ButtonGroup className="switch-buttons H">
                    <ToggleButton
                      id="schedule-toggle"
                      type="radio"
                      variant={
                        activeLatestContent === "schedule"
                          ? "primary"
                          : "outline-primary"
                      }
                      name="content-type"
                      value="schedule"
                      checked={activeLatestContent === "schedule"}
                      onChange={(e) => setActiveLatestContent("schedule")}
                      size="sm"
                    >
                      Schedule
                    </ToggleButton>
                    <ToggleButton
                      id="draft-toggle"
                      type="radio"
                      variant={
                        activeLatestContent === "draft"
                          ? "primary"
                          : "outline-primary"
                      }
                      name="draft-type"
                      value="draft"
                      checked={activeLatestContent === "draft"}
                      onChange={(e) => setActiveLatestContent("draft")}
                      size="sm"
                    >
                      Draft
                    </ToggleButton>
                  </ButtonGroup>
                </div>
                <div
                  className="d-flex gap-3 p-3 flex-column"
                  style={{ minHeight: "220px" }}
                >
                  {homeList?.isLoading ? (
                    [...Array(5)].map((_, index) => (
                      <div
                        key={index}
                        className="d-flex align-items-center gap-2"
                      >
                        <Skeleton type="thumbnail" width={50} height={28} />
                        <Skeleton type="text" width="70%" />
                      </div>
                    ))
                  ) : latestContentVideos?.length > 0 ? (
                    latestContentVideos
                      ?.slice(0, 5)
                      .map((video: any, index: number) => (
                        <div
                          key={video.id || index}
                          className="d-flex align-items-center gap-2"
                        >
                          <div
                            className="ratio ratio-16x9 border"
                            style={{ flex: "0 0 50px" }}
                          >
                            <img
                              src={
                                video?.images?.base +
                                  "/" +
                                  (video?.images?.thumb ??
                                    video?.images?.portrait) +
                                  THUMBNAIL_SIZE_SUFFIX +
                                  ".jpg" || FailureThumb
                              }
                              alt="Content thumbnail"
                              className="me-3 rounded w-100"
                              style={{
                                objectFit: "cover",
                              }}
                              onError={(e) => {
                                e.currentTarget.src = FailureThumb;
                              }}
                            />
                            {/* <i
                              className={`bi ${
                                activeLatestContent === 'schedule' ? 'bi-youtube' : 'bi-play-circle'
                              } socialIcon`}
                              style={{
                                color: activeLatestContent === 'schedule' ? 'red' : '#007bff',
                              }}
                            ></i> */}
                          </div>
                          <div
                            className="anchor text-truncate"
                            style={{
                              width: "calc(100% - 60px)",
                            }}
                          >
                            {video?.title || "No videos available"}
                          </div>
                        </div>
                      ))
                  ) : (
                    <NoContentFound
                      message="No recent content"
                      height="150px"
                    />
                  )}

                  {latestContentVideos?.length > 0 && (
                    <div className="text-end anchor border-top pt-1 H">
                      View All{" "}
                      <ChevronRight size={16} style={{ fill: "none" }} />
                    </div>
                  )}
                </div>
              </div>
              <div className="card">
                <div className="px-3 pt-3">
                  <div className="f-14 fw-medium text-uppercase">
                    Top Uploaders
                  </div>
                  <div className="f-10 text-muted fw-italic">Past 48 hours</div>
                </div>
                <div
                  className="d-flex gap-3 p-3 flex-column"
                  style={{ minHeight: "224px" }}
                >
                  {homeList?.isLoading ? (
                    [...Array(5)].map((_, index) => (
                      <div
                        key={index}
                        className="d-flex align-items-center gap-2"
                      >
                        <Skeleton type="avatar" width={28} height={28} circle />
                        <Skeleton type="text" width="40%" />
                        <div className="ms-auto">
                          <Skeleton type="text" width={30} />
                          <Skeleton type="text" width={50} />
                        </div>
                      </div>
                    ))
                  ) : homeList?.media_today_agg?.user_data?.length > 0 ? (
                    homeList?.media_today_agg?.user_data
                      ?.slice(0, 5)
                      .map((editor: any, index: number) => {
                        const getAvatarBgColor = () => {
                          const userName = editor.name || "unknown";
                          return getConsistentColor(userName);
                        };
                        const getAvatarInitials = () => {
                          const name = editor.name || "";
                          if (!name) return "";
                          const nameParts = name
                            .trim()
                            .split(" ")
                            .filter((part: string) => part.length > 0);

                          if (nameParts.length >= 2) {
                            return `${nameParts[0]
                              .charAt(0)
                              .toUpperCase()} ${nameParts[1]
                              .charAt(0)
                              .toUpperCase()}`;
                          } else if (nameParts.length === 1) {
                            return nameParts[0].charAt(0).toUpperCase();
                          }
                          return "";
                        };

                        return (
                          <div
                            key={editor.key}
                            className="d-flex align-items-center gap-2"
                          >
                            <div className="d-flex align-items-center gap-2">
                              <div
                                className="d-flex align-items-center justify-content-center rounded-circle"
                                style={{
                                  maxWidth: "28px",
                                  flex: "0 0 28px",
                                  height: "28px",
                                  backgroundColor: getAvatarBgColor(),
                                  color: "white",
                                  fontSize: "11px",
                                  wordSpacing: "-2px",
                                }}
                              >
                                {getAvatarInitials()}
                              </div>
                              <div className="text-dark">{editor.name}</div>
                            </div>

                            <span
                              className="progress-ratio ms-auto me-2"
                              style={{
                                width: `${editor.doc_count}%`,
                                maxWidth: "40%",
                              }}
                            ></span>
                            <div className="text-end">
                              <span className="fw-semibold text-primary">
                                {new Intl.NumberFormat("en-IN").format(
                                  editor.doc_count
                                )}
                              </span>
                            </div>
                          </div>
                        );
                      })
                  ) : (
                    <NoContentFound
                      message="No uploaders found"
                      height="150px"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-lg-3 col-md-4 col-sm-12">
          <QuickActions handleClick={handleClick} onFileEvent={onFileEvent} />
          {showCreateModal && (
            <Modal
              isOpen={showCreateModal}
              onClose={handleCreateClose}
              title={modalTitle}
              width="820px"
              height="auto"
              bgColor={!modalContent ? "bgGrey" : ""}
              closeOnOutsideClick={!modalContent ? true : false}
              footer={
                modalTitle === "CREATE > Premiere" ? (
                  <div className="d-flex justify-content-end">
                    <Button
                      type="primary"
                      isDisabled={premiereNextDisabled || premiereRpcPending}
                      onClick={() => {
                        const event = new CustomEvent("nextClickPremiere");
                        window.dispatchEvent(event);
                      }}
                    >
                      {premiereRpcPending ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          />
                          Loading...
                        </>
                      ) : (
                        "Next"
                      )}
                    </Button>
                  </div>
                ) : (
                  modalFooter
                )
              }
              updateModal={updateModal}
            >
              {modalContent || (
                <CreateOptions
                  closeModal={handleCreateClose}
                  updateModalContent={updateModalContent}
                  onPremiereNextDisabledChange={setPremiereNextDisabled}
                  onPremiereRpcPendingChange={setPremiereRpcPending}
                  openUpload={openUpload}
                  openPremiereEditor={(data: any) => setPremiereData(data)}
                  initialOption={initialOption}
                  onAssetTypeChange={setCurrentAssetType}
                />
              )}
            </Modal>
          )}
          {showWizardModal && (
            <Modal
              isOpen={showWizardModal}
              onClose={handleWizardClose}
              title={premiereData ? "PREMIERE > Metadata" : "Upload Video"}
              height="100vh"
              width="100%"
              closeOnOutsideClick={false}
              footer={
                <Button onClick={handleNextClick}>
                  {mediaDetailConfig.social_publish_page.enabled
                    ? "Save & Next"
                    : "Done"}
                </Button>
              }
              updateModal={updateModal}
            >
              <MediaDetails
                isWizard={true}
                premiereData={premiereData}
                closeHandler={() => {
                  updateIsClearFilter && updateIsClearFilter(true);
                  updateIsFilter && updateIsFilter(false);
                  navigateToMediaPage();
                  setShowWizardModal(false);
                  setPremiereData(null);
                  emptyMediaDetails();
                  emptyAssetDetails();
                }}
                closeModal={() => {
                  setShowWizardModal(false);
                  setPremiereData(null);
                }}
                openPublish={() => {
                  setShowCreateModal(false);
                  setShowWizardModal(false);
                  setPublishModal(true);
                }}
              />
            </Modal>
          )}
          <div className="card mb-3">
            <SocialDistribution
              CHART_SCRIPT_URL={CHART_SCRIPT_URL}
              updateModal={updateModal}
              homeList={homeList}
            />
          </div>
          <div className="card mb-3">
            <div className="px-3 pt-3">
              <div className="f-14 fw-medium text-uppercase">Top Channels</div>
              <div className="f-10 text-muted fw-italic ">Past 48 hours</div>
            </div>
            <div
              className="d-flex gap-3 p-3 flex-column"
              style={{ minHeight: "224px" }}
            >
              {homeList?.isLoading ? (
                [...Array(5)].map((_, index) => (
                  <div key={index} className="d-flex align-items-center gap-2">
                    <Skeleton type="avatar" width={28} height={28} circle />
                    <Skeleton type="text" width="40%" />
                    <div className="ms-auto">
                      <Skeleton type="text" width={30} />
                      <Skeleton type="text" width={50} />
                    </div>
                  </div>
                ))
              ) : homeList?.social_embeds_agg?.socialembeds?.top_names?.length >
                0 ? (
                homeList?.social_embeds_agg?.socialembeds?.top_names
                  ?.slice(0, 5)
                  .map((editor: any, index: number) => {
                    const getAvatarBgColor = () => {
                      const userName = editor.key || "unknown";
                      return getConsistentColor(userName);
                    };
                    const getAvatarInitials = () => {
                      const name = editor.key || "";
                      if (!name) return "";
                      const nameParts = name
                        .trim()
                        .split(" ")
                        .filter((part: string) => part.length > 0);

                      if (nameParts.length >= 2) {
                        return `${nameParts[0]
                          .charAt(0)
                          .toUpperCase()} ${nameParts[1]
                          .charAt(0)
                          .toUpperCase()}`;
                      } else if (nameParts.length === 1) {
                        return nameParts[0].charAt(0).toUpperCase();
                      }
                      return "";
                    };
                    return (
                      <div
                        key={editor.key}
                        className="d-flex align-items-center gap-2"
                      >
                        <div
                          className="d-flex align-items-center gap-2"
                          style={{ maxWidth: "50%" }}
                        >
                          {editor.image ? (
                            <img
                              src={editor.image}
                              alt="Channel thumbnail"
                              className="rounded-circle"
                              style={{ width: "28px", height: "28px" }}
                              onError={(e) => {
                                e.currentTarget.src = FailureThumb;
                              }}
                            />
                          ) : (
                            <div
                              className="d-flex align-items-center justify-content-center rounded-circle"
                              style={{
                                maxWidth: "28px",
                                flex: "0 0 28px",
                                height: "28px",
                                backgroundColor: getAvatarBgColor(),
                                color: "white",
                                fontSize: "11px",
                                wordSpacing: "-2px",
                              }}
                            >
                              {getAvatarInitials()}
                            </div>
                          )}
                          <div className="fw-medium text-dark">
                            {editor.key}
                          </div>
                        </div>
                        <span
                          className="progress-ratio ms-auto me-2"
                          style={{ width: `${editor.doc_count}%` }}
                        ></span>
                        <div className="text-end">
                          <span className="fw-semibold text-primary">
                            {new Intl.NumberFormat("en-IN").format(
                              editor.doc_count
                            )}
                          </span>
                        </div>
                      </div>
                    );
                  })
              ) : (
                <NoContentFound message="No channels found" height="150px" />
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="row mb-3 H">
        <div className="col-md-6 col-sm-12 bg-white">
          {/* Charts are now inside the tabs above */}
        </div>
        <div className="col-md-6 col-sm-12">
          <div className="card p-2">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h5 className="p-2">TRENDING VIDEOS</h5>
              <a href="/discover" className="view-all-link">
                View All →
              </a>
            </div>
            <div className="mediaedit_content_form p-3 flex-grow-1">
              <div className="row mt-4 w-100">
                {mediaList.list.length > 0 ? (
                  mediaList.list.slice(0, 3).map((item, index) => (
                    <div key={item.id} className="col-12 col-md-4">
                      <DiscoverCard
                        item={item}
                        onPreview={() => undefined}
                        useProgressiveLoading={true}
                        productList={bunchProductList as any}
                      />
                    </div>
                  ))
                ) : (
                  <div className="col-12">
                    <NoContentFound
                      message="No trending videos found"
                      height="200px"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row mb-3 H">
        <div className="col-md-12">
          <div className="card p-2">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h5 className="p-2">RECENTLY PUBLISHED</h5>
              <a href="/media" className="view-all-link">
                View All →
              </a>
            </div>
            <MediaList limit={3} />
          </div>
        </div>
      </div>

      <div className="row mb-3"></div>
      {publishModal && (
        <Modal
          isOpen={publishModal}
          title="Social Publish"
          onClose={() => {
            updateIsClearFilter && updateIsClearFilter(true);
            updateIsFilter && updateIsFilter(false);
            navigateToMediaPage();
            emptyAssetDetails();
            emptyMediaDetails();
            setPublishModal(false);
          }}
          height="100vh"
          width="100%"
          closeOnOutsideClick={false}
          updateModal={updateModal}
          footer={
            <div className="d-flex justify-content-between w-100">
              <Button type="outline-primary" onClick={handleBackClickEvent}>
                Back
              </Button>
              <Button onClick={handleDone}>Done</Button>
            </div>
          }
        >
          <MediaPublish
            isWizard={true}
            closeHandler={() => {
              updateIsClearFilter && updateIsClearFilter(true);
              updateIsFilter && updateIsFilter(false);
              navigateToMediaPage();
              setPublishModal(false);
              emptyMediaDetails();
              emptyAssetDetails();
            }}
          />
        </Modal>
      )}
    </div>
  );
};

const mapState = (state: RootState) => ({
  activeCustomUploadTab: state.media.activeCustomUploadTab,
  bunchProductList: state.constants.bunchProductList.list,
  mediaList: state.media.mediaList,
  homeList: state.media.homeList,
  currentMediaDetailsForEdit: state.media.mediaDetails.media,
});

const mapDispatch = {
  emptyAssetDetails,
  emptyMediaDetails,
  getMedia,
  callRegisterMediaOnCms,
  updateFileProgress,
  showUploadProgress,
  setUploadedSuccessfully,
  setSoftRefresh,
  decreaseCounter,
  getUpdateMedia,
  updateIsFilter,
  setisFilter,
  updateIsClearFilter,
  setMediaList,
  updateMediaStatus,
  updateActiveCustomUploadTab,
  updateModal,
  getHomeList,
};

const connector = connect(mapState, mapDispatch);
type Props = ConnectedProps<typeof connector>;

export default connector(Home);
